import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import type { PruefungsAntwortRequest, PruefungsAuswertung } from "@nl/shared"

import {
  useAntwortenPruefungsfrage,
  usePruefungsAuswertungen,
} from "@/lib/queries"

type PruefungChatValue = {
  threadId?: string
  /** Die Auswertung zu einem Werkzeugaufruf, sofern schon geantwortet wurde. */
  auswertungFuer: (toolCallId?: string) => PruefungsAuswertung | undefined
  antworten: (input: PruefungsAntwortRequest) => Promise<PruefungsAuswertung>
  /** Solange die gespeicherten Auswertungen noch geladen werden. */
  laedt: boolean
}

const PruefungChatContext = createContext<PruefungChatValue | null>(null)

/**
 * Der Zustand der Prüfungskarten im Gespräch.
 *
 * Zwei Quellen, bewusst zusammengeführt: die gespeicherten Auswertungen des
 * Gesprächs (damit eine Karte nach dem Neuladen nicht wieder unbeantwortet
 * dasteht) und die gerade abgegebene Antwort (damit das Ergebnis sofort auf
 * der Karte steht, ohne auf ein Nachladen zu warten).
 *
 * Schlüssel ist die `toolCallId`, nicht die Frage-ID: dieselbe Frage kann in
 * einem Gespräch zweimal drankommen, und dann sind es zwei Karten mit zwei
 * Zuständen.
 */
export function PruefungChatProvider({
  threadId,
  children,
}: {
  threadId?: string
  children: ReactNode
}) {
  const gespeichert = usePruefungsAuswertungen(threadId)
  const mutation = useAntwortenPruefungsfrage(threadId)
  const [frisch, setFrisch] = useState<Record<string, PruefungsAuswertung>>({})

  const antworten = useCallback(
    async (input: PruefungsAntwortRequest) => {
      const auswertung = (await mutation.mutateAsync({
        ...input,
        threadId,
      })) as PruefungsAuswertung
      if (input.toolCallId) {
        setFrisch((prev) => ({
          ...prev,
          [input.toolCallId as string]: auswertung,
        }))
      }
      return auswertung
    },
    [mutation, threadId]
  )

  const value = useMemo<PruefungChatValue>(() => {
    const geladen = new Map(
      (gespeichert.data ?? [])
        .filter((a) => !!a.toolCallId)
        .map((a) => [a.toolCallId as string, a])
    )
    return {
      threadId,
      laedt: gespeichert.isLoading,
      auswertungFuer: (toolCallId) =>
        toolCallId
          ? (frisch[toolCallId] ?? geladen.get(toolCallId))
          : undefined,
      antworten,
    }
  }, [gespeichert.data, gespeichert.isLoading, frisch, threadId, antworten])

  return (
    <PruefungChatContext.Provider value={value}>
      {children}
    </PruefungChatContext.Provider>
  )
}

/** Null außerhalb der Prüfungsvorbereitung; die Karte zeigt sich dann nur an. */
export function usePruefungChat(): PruefungChatValue | null {
  return useContext(PruefungChatContext)
}
